import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingCart, Menu, ChevronDown, Package, X } from "lucide-react";
import { useStoreSettings } from "@/hooks/useStoreSettings";

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("it_customer_user") || "null"); } catch { return null; }
};

export default function SiteHeader({
  cartCount = 0,
  cartTotal = 0,
  wishlistCount = 0,
  collections = [],
  menu = [],
  showNav = true,
  defaultSearch = "",
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState(defaultSearch);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = getUser();
  const storeSettings = useStoreSettings();
  const logoUrl = storeSettings?.logo_url;

  const handleSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      {/* ── Top header bar ── */}
      <header className="bg-[#0d0d1a] sticky top-0 z-40 shadow-md" data-testid="site-header">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-4 h-14 flex items-center gap-3 sm:gap-5">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center" data-testid="site-logo">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Store logo"
                className="h-10 w-auto max-w-[160px] object-contain"
                data-testid="site-logo-img"
              />
            ) : (
              <div className="flex flex-col leading-none">
                <span className="text-xl font-black tracking-tight leading-none">
                  <span className="text-orange-500">IMPORT</span>
                  <span className="text-white">REND</span>
                </span>
                <span className="text-[8px] text-slate-400 font-bold tracking-[0.18em] mt-0.5">WHOLESALE</span>
              </div>
            )}
          </Link>

          {/* Search bar (desktop) */}
          <form onSubmit={handleSearch} className="flex-1 hidden sm:flex">
            <div className="flex w-full h-10 rounded-md overflow-hidden bg-white">
              <input
                data-testid="site-search-input"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search For Products, Brands and more…"
                className="flex-1 px-4 text-sm text-slate-700 placeholder-slate-400 outline-none bg-white"
              />
              <button
                type="submit"
                data-testid="site-search-btn"
                className="bg-red-600 hover:bg-red-700 transition-colors px-4 flex items-center gap-2 flex-shrink-0"
              >
                <Search className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-semibold">Search</span>
              </button>
            </div>
          </form>

          {/* Right actions */}
          <div className="flex items-stretch divide-x divide-slate-700 ml-auto sm:ml-0 flex-shrink-0">
            {/* Account */}
            <Link
              to={user ? "/account" : "/customer-login"}
              data-testid="site-account-btn"
              className="flex items-center gap-2 px-2 sm:px-3 text-white hover:bg-white/5 transition-colors"
            >
              <User className="w-5 h-5 text-slate-300 flex-shrink-0" />
              <div className="hidden md:block text-left">
                <p className="text-[10px] text-slate-400 leading-tight">
                  {user ? `Hello, ${user.name?.split(" ")[0]}` : "Hello, Sign In"}
                </p>
                <p className="text-xs font-semibold leading-tight">Account</p>
              </div>
            </Link>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              data-testid="site-wishlist-btn"
              className="hidden sm:flex items-center gap-2 px-2 sm:px-3 text-white hover:bg-white/5 transition-colors"
            >
              <Heart className={`w-5 h-5 flex-shrink-0 ${wishlistCount > 0 ? "fill-red-500 text-red-500" : "text-slate-300"}`} />
              <div className="hidden md:block text-left">
                <p className="text-[10px] text-slate-400 leading-tight">Saved</p>
                <p className="text-xs font-semibold leading-tight">
                  Wishlist{wishlistCount > 0 ? ` (${wishlistCount})` : ""}
                </p>
              </div>
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              data-testid="site-cart-btn"
              className="flex items-center gap-2 px-2 sm:px-3 text-white hover:bg-white/5 transition-colors"
            >
              <div className="relative flex-shrink-0">
                <ShoppingCart className="w-5 h-5 text-slate-300" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-[10px] text-slate-400 leading-tight">
                  ₹{Number(cartTotal || 0).toLocaleString("en-IN")}
                </p>
                <p className="text-xs font-semibold leading-tight">Cart</p>
              </div>
            </Link>

            {/* Mobile menu toggle */}
            <button
              className="sm:hidden px-3 text-slate-300 hover:text-white"
              onClick={() => setMobileOpen(v => !v)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="sm:hidden px-3 pb-2">
          <form onSubmit={handleSearch} className="flex h-9 rounded-md overflow-hidden bg-white">
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search…"
              className="flex-1 px-3 text-sm outline-none text-slate-700 placeholder-slate-400"
            />
            <button type="submit" className="bg-red-600 px-3 flex items-center">
              <Search className="w-4 h-4 text-white" />
            </button>
          </form>
        </div>
      </header>

      {/* ── Nav bar ── */}
      {showNav && (
        <nav className="bg-slate-900 text-white relative z-30">
          <div className="max-w-[1800px] mx-auto px-3 sm:px-4">
            <div className="flex items-center">
              {/* Categories dropdown */}
              {collections.length > 0 && (
                <div className="relative flex-shrink-0">
                  <button
                    data-testid="nav-category-btn"
                    onClick={() => setCatOpen(v => !v)}
                    onBlur={() => setTimeout(() => setCatOpen(false), 150)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    <Menu className="w-4 h-4" />
                    Category
                    <ChevronDown className={`w-3 h-3 transition-transform ${catOpen ? "rotate-180" : ""}`} />
                  </button>
                  {catOpen && (
                    <div className="absolute top-full left-0 bg-white text-slate-800 shadow-2xl rounded-b-xl min-w-[220px] py-2 z-50 border border-slate-100">
                      {collections.map((col, i) => (
                        <Link
                          key={i}
                          to={`/c/${col.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                          onClick={() => setCatOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <Package className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          {col}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Menu items */}
              <div className="hidden md:flex items-center flex-1 overflow-x-auto scrollbar-hide">
                {menu.map((item) => (
                  <Link
                    key={item.id || item.label}
                    to={item.url || "/"}
                    className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors flex-shrink-0"
                  >
                    {item.label}
                    {item.badge && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white"
                        style={{ background: item.badge_color || "#dc2626" }}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileOpen && menu.length > 0 && (
            <div className="md:hidden bg-slate-800 border-t border-slate-700 px-3 pb-3 pt-1">
              {menu.map((item) => (
                <Link
                  key={item.id || item.label}
                  to={item.url || "/"}
                  onClick={() => setMobileOpen(false)}
                  className="block py-2 text-sm text-slate-300 hover:text-white"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </nav>
      )}
    </>
  );
}
